// As pastas, à esquerda. Segue o mockup: pílula arredondada à direita, a pasta
// ativa em fundo teal claro.
//
// NO CELULAR ela é uma GAVETA, não uma coluna. Fixa, ela comia metade da tela
// e sobrava uma faixa para a lista de mensagens. O mesmo botão de menu que
// recolhe no desktop abre a gaveta no celular — um controle só, porque dois
// botões para a mesma ideia é o que faz o usuário não achar nenhum.

import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import * as correio from "../api/correio";
import type { Pasta } from "../api/tipos";
import { Icone, type NomeDeIcone } from "../ui/Icone";
import { SeloNovidade, novidadeUsada } from "../ui/SeloNovidade";
import { tamanho } from "../ui/formato";
import { corDoMarcador } from "./Marcadores";
import { AcoesDaPasta, GerirPastas, podeMexer } from "./Pastas";
import { useCelular } from "../ui/tela";

/**
 * Ícone por USO ESPECIAL, nunca por nome.
 *
 * Numa instalação migrada a pasta de enviados pode se chamar "Elementos
 * enviados". Comparar nome daria a ela o ícone de pasta genérica — e o usuário
 * perderia a única pista visual de que aquela é a pasta que ele procura.
 */
const ICONE_POR_USO: Record<string, NomeDeIcone> = {
  "\\Drafts": "FileText",
  "\\Sent": "Send",
  "\\Junk": "ShieldAlert",
  "\\Trash": "Trash2",
  "\\Archive": "Archive",
};

function iconeDa(pasta: Pasta): NomeDeIcone {
  if (pasta.nome === "INBOX") return "Inbox";
  // PASTA NÃO É MARCADOR, e desenhá-la com etiqueta dizia que era.
  //
  // Medido em 30/10/2026, com o Jhou na frente: ele tinha uma PASTA chamada
  // `jhou`, viu o ícone de etiqueta na barra lateral, abriu "Pôr um marcador"
  // e não a encontrou — porque marcador é keyword do IMAP e aquilo era pasta.
  // Se tivesse clicado em aplicar, teria criado um marcador `jhou` DIFERENTE
  // da pasta de mesmo nome, e a mensagem não iria para onde ele via na tela.
  //
  // Duas coisas com o mesmo nome, uma invisível. O ícone era a primeira peça
  // da confusão, e é a mais barata de tirar.
  return ICONE_POR_USO[pasta.uso_especial ?? ""] ?? "Folder";
}

export function BarraLateral({
  recolhida,
  aberta,
  aoFechar,
  aoEscrever,
}: {
  /** Desktop: coluna estreita só com ícones. */
  recolhida: boolean;
  /** Celular: a gaveta está aberta. */
  aberta: boolean;
  aoFechar: () => void;
  aoEscrever: () => void;
}) {
  const fila = useQueryClient();
  const navegar = useNavigate();
  const [sobre, setSobre] = useState<string | null>(null);
  // As pastas junto com a lista: o contador de não lidas fica errado se a
  // mensagem aparece na INBOX e o "3" ao lado dela continua dizendo "2".
  const { data } = useQuery({
    queryKey: ["pastas"],
    queryFn: () => correio.pastas(),
    refetchInterval: 10_000,
  });
  const pastas = data ? correio.ordenar(data.pastas) : [];
  const cota = data?.cota;

  // DEFEITO REAL, achado pelo Jhou no celular: a gaveta abria SÓ COM ÍCONES
  // na primeira vez e completa na segunda.
  //
  // A causa é que `recolhida` é estado de DESKTOP — a coluna estreita — e o
  // mesmo botão mexia nos dois. Eu tinha tratado só a LARGURA da gaveta e
  // deixado os rótulos, o "Nova pasta" e a cota ainda presos ao `recolhida`.
  // Quem abre um menu de celular e vê seis ícones sem nome não descobre que
  // basta fechar e abrir de novo: conclui que o aplicativo está quebrado.
  //
  // Agora a ideia existe UMA vez: no celular nunca há coluna estreita.
  const celular = useCelular();
  const estreita = recolhida && !celular;

  // A LARGURA SAI DAS CLASSES e vira estilo, de propósito.
  //
  // Ela depende de DOIS estados independentes — gaveta no celular, coluna
  // recolhida no desktop — e com classes isso virava três utilitários de
  // largura disputando (`w-64` da gaveta contra `w-16`/`w-60` da coluna).
  // Quem vence nesse caso não é a ordem em que estão escritas e sim a ordem
  // do CSS que o Tailwind gera: a coluna do desktop simplesmente não
  // encolhia, sem erro nenhum, e o mesmo valor aparecia certo num elemento
  // de teste com as mesmas classes.
  //
  // Um valor, um lugar. Tailwind para o resto.
  const largura = celular ? "16rem" : estreita ? "4rem" : "15rem";

  return (
    <>
      {/* A cortina fecha a gaveta ao tocar fora — o gesto que todo mundo já
          tenta antes de procurar o botão de fechar. */}
      {aberta && (
        <button
          type="button"
          aria-label="Fechar menu"
          onClick={aoFechar}
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
        />
      )}

      <aside
        onClick={aoFechar}
        style={{ width: largura }}
        className={[
          // A transição fica SÓ no transform, que é o deslize da gaveta.
          // Animar `width` não funcionava — a coluna ficava presa no valor
          // antigo — e é errado de qualquer jeito: largura recalcula o
          // layout a cada quadro, enquanto transform não toca no layout.
          "flex flex-col gap-1 pt-3 pr-3 transition-transform",
          // Celular: gaveta por cima do conteúdo.
          "fixed inset-y-0 left-0 z-40 bg-linha overflow-y-auto",
          aberta ? "translate-x-0" : "-translate-x-full",
          // Desktop: volta a ser coluna no fluxo, sempre visível.
          "md:static md:z-auto md:translate-x-0 md:overflow-visible md:flex-shrink-0",
        ].join(" ")}
      >
      <button
        onClick={aoEscrever}
        className="ml-2 mb-3 flex items-center gap-3 rounded-2xl bg-white px-4 py-3 shadow hover:shadow-md text-sm font-medium text-tinta"
        title="Escrever (c)"
      >
        <Icone nome="Pencil" tamanho={18} className="text-acao" />
        {!estreita && "Escrever"}
      </button>

      <nav className="flex-1 overflow-y-auto">
        {pastas.map((p) => (
          <NavLink
            key={p.nome}
            to={`/correio/${encodeURIComponent(p.nome!)}`}
            onMouseEnter={() => setSobre(p.nome ?? null)}
            onMouseLeave={() => setSobre(null)}
            className={({ isActive }) =>
              `flex items-center gap-4 rounded-r-full pl-6 pr-3 py-1.5 text-sm ${
                isActive ? "bg-acao-clara font-semibold text-acao-escura" : "text-tinta-media hover:bg-linha"
              }`
            }
            title={estreita ? p.rotulo : undefined}
            // RECOLHIDA, O LINK É SÓ UM ÍCONE: o leitor de tela anunciava
            // "link" e nada mais, e a barra inteira virava seis links
            // indistinguíveis. O nome vem SEMPRE, não só quando estreita —
            // com o rótulo visível ele não atrapalha, e depender do estado
            // da barra para ter nome acessível é como ele se perde de novo.
            aria-label={p.rotulo || p.nome || undefined}
          >
            <Icone nome={iconeDa(p)} tamanho={18} />
            {!estreita && (
              <>
                <span className="flex-1 text-left truncate">{p.rotulo || p.nome}</span>
                {/* As ações só aparecem no ponteiro, e só nas pastas do
                    usuário: um lápis e uma lixeira ao lado de "Caixa de
                    entrada" convidam a um clique que o servidor vai recusar. */}
                {sobre === p.nome && podeMexer(p.nome) ? (
                  <AcoesDaPasta
                    nome={p.nome!}
                    aoMudar={() => {
                      void fila.invalidateQueries({ queryKey: ["pastas"] });
                      void fila.invalidateQueries({ queryKey: ["mensagens"] });
                      navegar("/correio/INBOX");
                    }}
                  />
                ) : (
                  /* Só a contagem de NÃO LIDAS, e só quando houver. O total de
                     mensagens numa pasta não é informação acionável, e enche a
                     barra de números que ninguém lê. */
                  !!p.nao_lidas && <span className="text-xs tabular-nums">{p.nao_lidas}</span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* PROGRAMADOS (apontamento 22).
          Fica junto das pastas porque é o que a pessoa procura: ela
          programou um envio e quer voltar nele. NÃO é pasta IMAP — a
          mensagem ainda não existe no Dovecot —, e por isso a entrada é um
          link para uma tela própria.
          Só aparece quando há algo programado. Uma entrada permanente que
          abre vazia em toda caixa é exatamente o que a revisão apontou no
          menu do painel (item 36): item de menu para coisa que não existe. */}
      <Programados estreita={estreita} />

      {/* MARCADORES, LOGO DEPOIS DAS PASTAS.
          Apontamento 21 da revisão de UX: "não há onde ver ou filtrar por
          marcador". Os marcadores existiam, eram aplicados, apareciam na
          linha da mensagem — e não havia lugar nenhum que respondesse
          "quais são os meus" nem "me mostre tudo que tem este".
          Ficam aqui, abaixo das pastas, porque é o mesmo gesto: escolher um
          recorte da caixa. Só aparecem quando existe algum — uma seção
          "Marcadores" vazia em toda caixa nova ensinaria a ignorá-la. */}
      {!estreita && <Marcadores />}

      {/* ALGUM E-MAIL SUMIU? — as duas telas de diagnóstico.
          
          Ficam aqui, acima de Contatos, e não escondidas em Configurações:
          quem procura por elas está com um problema AGORA, e procurar é a
          última coisa que vai querer fazer. O rótulo é a frase que a pessoa
          diria em voz alta — "não recebi um e-mail" —, não o nome técnico do
          que está por trás (quarentena, fila, eventos de entrega).

          É a mesma lição de "alias" contra "encaminhador": recurso que existe
          e não é encontrado não existe. */}
      {!estreita && (
        <p className="mt-4 px-6 pb-1 text-[11px] font-semibold uppercase tracking-wider text-tinta-fraca">
          Algum e-mail sumiu?
        </p>
      )}

      <NavLink
        to="/nao-recebi"
        className={({ isActive }) =>
          `flex items-start gap-4 rounded-r-full py-1.5 pl-6 pr-3 text-sm ${
            isActive
              ? "bg-acao-clara font-semibold text-acao-escura"
              : "text-tinta-media hover:bg-linha"
          }`
        }
        title={estreita ? "Não recebi um e-mail" : undefined}
        onClick={() => novidadeUsada("nao-recebi")}
      >
        <Icone nome="ShieldAlert" tamanho={18} className="mt-0.5 flex-shrink-0" />
        {!estreita && (
          <span>
            <span className="block">
              Não recebi um e-mail
              <SeloNovidade id="nao-recebi" />
            </span>
            <span className="block text-[11px] font-normal text-tinta-fraca">
              Veja se ficou retido
            </span>
          </span>
        )}
      </NavLink>

      <NavLink
        to="/meu-email-chegou"
        className={({ isActive }) =>
          `flex items-start gap-4 rounded-r-full py-1.5 pl-6 pr-3 text-sm ${
            isActive
              ? "bg-acao-clara font-semibold text-acao-escura"
              : "text-tinta-media hover:bg-linha"
          }`
        }
        title={estreita ? "Meu e-mail chegou?" : undefined}
        onClick={() => novidadeUsada("meu-email-chegou")}
      >
        <Icone nome="Send" tamanho={18} className="mt-0.5 flex-shrink-0" />
        {!estreita && (
          <span>
            <span className="block">
              Meu e-mail chegou?
              <SeloNovidade id="meu-email-chegou" />
            </span>
            <span className="block text-[11px] font-normal text-tinta-fraca">
              Acompanhe o que você enviou
            </span>
          </span>
        )}
      </NavLink>

      {/* A agenda fica com as pastas porque é onde se procura por ela — e
          não no menu da conta, que é onde ficam as configurações. */}
      <NavLink
        to="/contatos"
        className={({ isActive }) =>
          `mt-4 flex items-center gap-4 rounded-r-full py-1.5 pl-6 pr-3 text-sm ${
            isActive
              ? "bg-acao-clara font-semibold text-acao-escura"
              : "text-tinta-media hover:bg-linha"
          }`
        }
        title={estreita ? "Contatos" : undefined}
      >
        <Icone nome="Users" tamanho={18} />
        {!estreita && "Contatos"}
      </NavLink>

      {!estreita && <GerirPastas pastas={pastas} />}

      {!estreita && cota && cota.limite_bytes! > 0 && (
        <div className="px-6 py-3 text-xs text-tinta-fraca">
          {/* A BARRA EXISTIA E NINGUÉM A VIA: 4px de altura num tom quase
              igual ao fundo. A revisão de UX de 30/10/2026 a reportou como
              ausente, e quem revisa olhando a tela tem razão — barra que não
              se enxerga é o mesmo que texto sozinho. */}
          <div
            className="h-2 overflow-hidden rounded-full bg-linha-suave ring-1 ring-inset ring-linha"
            title={`${Math.round(cota.percentual ?? 0)}% usado`}
          >
            <div
              className={`h-full ${cota.percentual! >= 90 ? "bg-perigo" : "bg-acao"}`}
              style={{ width: `${Math.max(2, Math.min(100, cota.percentual ?? 0))}%` }}
            />
          </div>
          <p className="mt-1.5">
            {tamanho(cota.usado_bytes)} de {tamanho(cota.limite_bytes)}
          </p>
        </div>
      )}
      </aside>
    </>
  );
}


/**
 * A seção de marcadores da barra lateral.
 *
 * NÃO HÁ TABELA DE MARCADORES, e continua não havendo: eles são keywords do
 * IMAP, vivem na mensagem, e aparecem no Thunderbird e no celular. A lista
 * vem de `GET /correio/marcadores`, que PERGUNTA ao índice quais estão em
 * uso — a única resposta verdadeira para "quais são os meus marcadores".
 *
 * Clicar leva à BUSCA com `marcador:Nome`, e não a uma rota nova: marcador
 * atravessa pastas (é o ponto dele), e a listagem de mensagens é sempre de
 * UMA pasta. A busca já responde em todas.
 */
function Marcadores() {
  const consulta = useQuery({
    queryKey: ["marcadores-em-uso"],
    queryFn: ({ signal }) => correio.marcadoresEmUso(signal),
    // Mesmo intervalo das pastas: a contagem muda quando chega mensagem
    // marcada por filtro, e um número velho na lateral é pior que nenhum.
    refetchInterval: 60_000,
  });
  const itens = consulta.data?.itens ?? [];
  if (itens.length === 0) return null;

  return (
    <>
      <p className="mt-4 px-6 pb-1 text-[11px] font-semibold uppercase tracking-wider text-tinta-fraca">
        Marcadores
      </p>
      {itens.map((m) => (
        <NavLink
          key={m.nome}
          to={`/busca?q=${encodeURIComponent(`marcador:${m.nome}`)}`}
          className="flex items-center gap-3 rounded-r-full py-1.5 pl-6 pr-3 text-sm text-tinta-media hover:bg-linha"
          title={`${m.mensagens} mensagem(ns) com "${m.nome}"`}
        >
          {/* O quadradinho de cor é o MESMO da etiqueta na mensagem, pela
              mesma função: é o que liga a linha da lateral à etiqueta que a
              pessoa vê na lista. */}
          <span
            aria-hidden="true"
            className="h-2.5 w-2.5 flex-shrink-0 rounded"
            style={{ background: corDoMarcador(m.nome) }}
          />
          <span className="min-w-0 flex-1 truncate text-left">{m.nome}</span>
          <span className="text-xs tabular-nums text-tinta-fraca">
            {m.nao_lidas > 0 ? m.nao_lidas : ""}
          </span>
        </NavLink>
      ))}
    </>
  );
}


/**
 * A entrada de "Programados" na barra lateral.
 *
 * Só desenha quando existe envio programado. O número é o que ainda VAI
 * sair — contar os já enviados dos últimos 7 dias (que a rota também
 * devolve, de propósito) faria a lateral dizer "3" para quem não tem nada
 * pendente.
 */
function Programados({ estreita }: { estreita: boolean }) {
  const consulta = useQuery({
    queryKey: ["agendados"],
    queryFn: () => correio.agendados(),
    refetchInterval: 60_000,
  });
  const aSair = (consulta.data ?? []).filter(
    (a) => a.estado === "agendado" || a.estado === "enviando",
  );
  if (aSair.length === 0) return null;

  return (
    <NavLink
      to="/programados"
      className={({ isActive }) =>
        `flex items-center gap-4 rounded-r-full py-1.5 pl-6 pr-3 text-sm ${
          isActive
            ? "bg-acao-clara font-semibold text-acao-escura"
            : "text-tinta-media hover:bg-linha"
        }`
      }
      // Na coluna estreita sobra só o ícone, e sem o `title` ele não diz
      // nada: é a mesma correção do apontamento 11, sobre links de pasta
      // lidos como "link".
      title={`Programados — ${aSair.length} a sair`}
    >
      <Icone nome="CalendarClock" tamanho={18} className="flex-shrink-0" />
      {!estreita && (
        <>
          <span className="min-w-0 flex-1 truncate text-left">Programados</span>
          <span className="text-xs tabular-nums">{aSair.length}</span>
        </>
      )}
    </NavLink>
  );
}
